import {
  Box,
  IconButton,
  Link as MuiLink,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LinkItem } from './types';

interface LinkRowProps {
  link: LinkItem;
  editMode: boolean;
  onEdit: (link: LinkItem) => void;
  onDelete: (link: LinkItem) => void;
}

const LinkRow = ({ link, editMode, onEdit, onDelete }: LinkRowProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: link.id, disabled: !editMode });

  if (!editMode) {
    return (
      <ListItemButton component={MuiLink} href={link.to} target="_blank" rel="noopener noreferrer">
        <ListItemText primary={link.label} primaryTypographyProps={{ variant: 'body2' }} />
      </ListItemButton>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.25,
        pr: 0.5,
        borderRadius: 1.5,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
        bgcolor: isDragging ? 'action.selected' : 'transparent',
      }}
    >
      <IconButton
        ref={setActivatorNodeRef}
        size="small"
        aria-label={`Drag ${link.label}`}
        sx={{ cursor: 'grab', touchAction: 'none', color: 'text.secondary' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }} title={link.to}>
        {link.label}
      </Typography>

      <Tooltip title="Edit">
        <IconButton size="small" aria-label={`Edit ${link.label}`} onClick={() => onEdit(link)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" aria-label={`Delete ${link.label}`} onClick={() => onDelete(link)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default LinkRow;
